import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Filler,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';
import ChartDataLabels from 'chartjs-plugin-datalabels';
import moment from 'moment';
import PropTypes from 'prop-types';
import React, { useContext, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Bar, Doughnut, Line } from 'react-chartjs-2';
import { useTranslation } from 'react-i18next';
import {
  FiRefreshCw
} from 'react-icons/fi';
import { RegulationContext } from '../../../App';
import DashGraphConfigService from '../../../core/service/DashGraphConfigService';
import {
  formatFyChartXAxisLabel,
  isFyYearlyXAxisValue,
} from '../../../core/utilities/assessmentPeriodUtils';
import Utils from '../../../core/utilities/Utils';
import { GRAPH_FRACTION_DIGITS, LS_COMPANY_INFO_KEY } from '../../../core/constants/common';
import './MaximizedChartCard.scss';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ChartDataLabels
);

const MaximizedChartCard = React.forwardRef(({
  config,
  year,
  title,
  type = 'LINE',
  height = 600, // Increased default height for better popup utilization
  showLegend = true,
  showGrid = false,
  showTooltip = true,
  colors = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4'],
  filters = {},
  onDataUpdate,
  standardId,
  showLabels: showLabelsProp,
  onShowLabelsChange
}, ref) => {
  const { t, i18n } = useTranslation();
  const { glbRegulation } = useContext(RegulationContext);

  // Add chart ref for download functionality
  const chartRef = useRef(null);

  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [internalShowLabels, setInternalShowLabels] = useState(false);
  const isShowLabelsControlled = showLabelsProp !== undefined;
  const showLabels = isShowLabelsControlled ? showLabelsProp : internalShowLabels;
  const [chartData, setChartData] = useState(null);
  const [chartType, setChartType] = useState(type);
  const [yAxisMax, setYAxisMax] = useState(null);

  const roundChartNumericValue = (value) =>
    parseFloat(Utils._roundNumericValue(parseFloat(value), GRAPH_FRACTION_DIGITS));

  const sumStackedValues = (values) => {
    const multiplier = Math.pow(10, GRAPH_FRACTION_DIGITS);
    const scaledTotal = values.reduce((sum, val) => {
      if (val === null || val === undefined || isNaN(val)) return sum;
      return sum + Math.round(parseFloat(val) * multiplier);
    }, 0);
    return roundChartNumericValue(scaledTotal / multiplier);
  };

  // Helper function to format numbers with comma separators
  // If decimal places are all zeros, display without decimals, otherwise show graph fraction digits
  const formatNumberWithCommas = (value) => {
    if (value === null || value === undefined || isNaN(value)) {
      return '0';
    }

    const rounded = roundChartNumericValue(value);
    const decimalPart = Math.abs(rounded % 1);
    const isAllZeros = decimalPart < Math.pow(10, -(GRAPH_FRACTION_DIGITS + 1));

    if (Number.isInteger(rounded) || isAllZeros) {
      return rounded.toLocaleString('en-US');
    }

    return rounded.toLocaleString('en-US', {
      minimumFractionDigits: GRAPH_FRACTION_DIGITS,
      maximumFractionDigits: GRAPH_FRACTION_DIGITS
    });
  };
  const [kpiUom, setKpiUom] = useState('');
  const [activeInterval, setActiveInterval] = useState('MONTHLY');
  const [kpiColor, setKpiColor] = useState('');

  // Summary data state
  const [summaryData, setSummaryData] = useState({
    total: 0,
    percentageChange: 0,
    maxMonth: '',
    minMonth: '',
    unit: ''
  });

  // Get user context
  const authority = JSON.parse(localStorage.getItem('authority'));
  const companyId = authority?.profile?.company?.id;
  const companyInfo = JSON.parse(localStorage.getItem(LS_COMPANY_INFO_KEY) || '{}');
  const dataFreqType = companyInfo?.frequencyType ?? companyInfo?.dataFrequencyType;
  const isFinancialYear = dataFreqType === 'FY';
  // Use passed standardId prop or fallback to context
  const finalStandardId = standardId || glbRegulation?.id;

  useEffect(() => {    
    if (config && companyId && finalStandardId && year) {
      fetchChartData();
    } else {
      setIsLoading(false);
    }
  }, [config, companyId, finalStandardId, year, activeInterval]);

  const fetchChartData = async (filterOverrides) => {
    if (!config?.id || !companyId || !finalStandardId) {
      setIsLoading(false);
      return;
    }

    const activeFilters = filterOverrides ?? filters;
    const chartYear = activeFilters.periodFrom
      ? activeFilters.periodFrom.getFullYear()
      : year;

    setIsLoading(true);
    setError(null);

    try {
      const period = `${chartYear}-01-01`;
      const businessUnitIds = [];
      if (activeFilters.parentEntities && activeFilters.parentEntities.length > 0) {
        businessUnitIds.push(...activeFilters.parentEntities.map(entity => entity.id));
      }
      if (activeFilters.childEntities && activeFilters.childEntities.length > 0) {
        businessUnitIds.push(...activeFilters.childEntities.map(entity => entity.id));
      }
      const periodFrom = activeFilters.periodFrom ?
        moment(activeFilters.periodFrom).format('YYYY-MM-DD') : '';
      const periodTo = activeFilters.periodTo ?
        moment(activeFilters.periodTo).format('YYYY-MM-DD') : '';
      const apiStandardId = finalStandardId;
      const apiGraphId = config.id;
      // Use YEARLY interval for pie charts
      const chartType = config.type || 'BAR';
      const intervalForApi = (chartType === 'PIE' || chartType === 'DOUGHNUT') ? 'YEARLY' : activeInterval;
      const dataObject = {
        standardId: apiStandardId,
        graphId: apiGraphId,
        period: period,
        categoryType: '',
        businessUnitIds: businessUnitIds.join(','),
        periodFrom: periodFrom,
        periodTo: periodTo,
        interval: intervalForApi,
        offset: '',
        aggregationType: '',
        includeTrends: ''
      };
      const response = await DashGraphConfigService.getDataView(dataObject);
      if (response.status === 200 && response.data) {
        const graphData = response.data.data || response.data;
        processChartData(graphData);
      // Notify parent component about data update with extracted IDs
      if (onDataUpdate) {
          const extractedData = {
            ...response.data,
            sankeyTree: JSON.parse(graphData.metadata?.sankeyTree || '[]')
          };
          onDataUpdate(extractedData);
        }
      } else {
        throw new Error('No data received from API');
      }
    } catch (error) {
      setError(t("errorLoadingChartData.label"));
      generateSampleData();
    } finally {
      setIsLoading(false);
      }
    };

    // Helper function to format numbers properly - comma separators and 4 decimals for fractional
    // If decimal places are all zeros, display without decimals
    const formatNumber = (value) => {
      return formatNumberWithCommas(value);
    };

  // Download functionality for maximized chart
  const downloadChartImageMaximized = (graphTitle) => {
    try {
      const chartInstance = chartRef.current;
      if (chartInstance) {
        const originalWidth = chartInstance.width;
        const originalHeight = chartInstance.height;
  
        // Resize chart for download
        chartInstance.resize(1200, 800);
        const chart = chartInstance.canvas;
        const context = chart.getContext('2d');
        
        // Save the current canvas state
        context.save();
        
        // Create a new canvas with extra space for title and total
        const downloadCanvas = document.createElement('canvas');
        const downloadContext = downloadCanvas.getContext('2d');
        
        // Set canvas size with extra space for title and total (100px top padding)
        downloadCanvas.width = chart.width;
        downloadCanvas.height = chart.height + 100;
        
        // Fill background with white
        downloadContext.fillStyle = '#ffffff';
        downloadContext.fillRect(0, 0, downloadCanvas.width, downloadCanvas.height);
        
        // Draw the original chart on the new canvas (offset by 100px to make room for title)
        downloadContext.drawImage(chart, 0, 100);
        
        // Add title and total text
        const titleText = graphTitle || title || config?.title || 'Chart';
        const totalText = chartType !== 'KPI' 
          ? `Total: ${formatNumber(summaryData.total)} ${summaryData.unit}`
          : `Value: ${summaryData.total.toLocaleString()} ${summaryData.unit}`;
        
        // Configure text styling
        downloadContext.fillStyle = '#1e293b'; // Dark text color
        downloadContext.textAlign = 'center';
        downloadContext.textBaseline = 'top';
        
        // Draw title
        downloadContext.font = 'bold 26px Inter, -apple-system, BlinkMacSystemFont, sans-serif';
        downloadContext.fillText(titleText, downloadCanvas.width / 2, 20);
        
        // Draw total
        downloadContext.font = '24px Inter, -apple-system, BlinkMacSystemFont, sans-serif';
        downloadContext.fillText(totalText, downloadCanvas.width / 2, 55);
        
        // Convert to image and download
        const url = downloadCanvas.toDataURL('image/jpeg', 0.9);
        const link = document.createElement('a');
        link.href = url;
        link.download = `${graphTitle || 'chart'}.jpg`;
        link.click();
        
        // Restore original chart size
        context.restore();
        chartInstance.resize(originalWidth, originalHeight);
      }else {
        console.warn('Chart instance not available for download');
      }
    } catch (error) {
      console.error('Error downloading chart:', error);
    }
  };

  const handleDownload = () => {
    const graphTitle = title || config?.title || 'maximized-chart';
    downloadChartImageMaximized(graphTitle);
  };

  // Expose download and fetch to parent (e.g. filter apply / reset)
  useImperativeHandle(ref, () => ({
    downloadChart: handleDownload,
    fetchChartData
  }));

  const processChartData = (graphData) => {    
    // Handle different response structures
    let processedData = graphData;
    // Handle the new response structure with data wrapper
    if (graphData && graphData.data) {
      processedData = graphData.data;
    } else if (graphData && graphData.properties) {
      processedData = graphData;
    } else if (graphData && graphData.dataPoints) {
      processedData = graphData;
    } else {
      generateSampleData();
      return;
    }
    if (!processedData || !processedData.dataPoints || processedData.dataPoints.length === 0) {
      generateSampleData();
      return;
    }
    // Process footer data for summary section
    if (processedData.footers && processedData.footers.length > 0) {
      processFooterData(processedData.footers);
    }
    // Determine chart type from the data structure
    const chartType = processedData.type || config.type || 'BAR';    
    // Normalize chart type for case-insensitive comparison
    const normalizedChartType = chartType?.toUpperCase();
    switch (normalizedChartType) {
      case 'LINE':
        setupLineChartData(processedData);
        break;
      case 'AREA':
        setupAreaChartData(processedData);
        break;
      case 'BAR':
      case 'STACKED BAR':
      case 'HORIZONTAL BAR':
      case 'HORIZONTAL STACKED BAR':
        setupBarChartData(processedData);
        break;
      case 'PIE':
      case 'DOUGHNUT':
        setupPieChartData(processedData);
        break;
      case 'KPI':
        setupKPIChartData(processedData);
        break;
      default:
        setupBarChartData(processedData);
    }
  };

  // X-axis label from data point: "April 2025"
  const makePointLabel = (xValue, year) => `${xValue} ${year}`;

  const getChartLabels = (dataPoints) => {
    if (filters?.periodFrom && filters?.periodTo) {
      return getAllMonthLabelsInRange(filters.periodFrom, filters.periodTo);
    }
    return dataPoints.map((dp) =>
      isFyYearlyXAxisValue(dp.xValue)
        ? formatFyChartXAxisLabel(dp.xValue, dp.year, isFinancialYear)
        : makePointLabel(dp.xValue, dp.year)
    );
  };

  const getMetricValueAtLabel = (dataPoints, label, metricLabel) => {
    const point = dataPoints.find((dp) => {
      const displayLabel = isFyYearlyXAxisValue(dp.xValue)
        ? formatFyChartXAxisLabel(dp.xValue, dp.year, isFinancialYear)
        : makePointLabel(dp.xValue, dp.year);
      return displayLabel === label;
    });
    if (!point) return 0;

    const yValue = point.yValues?.find(y => y.label === metricLabel);
    let value = yValue?.value ?? 0;

    const currentYear = new Date().getFullYear();
    const currentMonth = new Date().getMonth();
    if (point.year === currentYear) {
      const monthIndex = moment(point.xValue, ['MMMM', 'MMM'], true).month();
      if (monthIndex > currentMonth) {
        value = 0;
      }
    }

    return value;
  };

  const buildBarLineChartData = (graphData, defaultChartType) => {
    let dataPoints = [...(graphData.dataPoints || [])].sort((a, b) => a.order - b.order);
    const dataPointOptions = graphData.dataPointOptions?.filter(o => !o.position) || [];

    if (dataPoints.length > 24) {
      dataPoints = dataPoints.slice(0, 24);
    }

    const isLine = defaultChartType === 'LINE';
    const labels = getChartLabels(dataPoints);

    const datasets = dataPointOptions.map((option, index) => {
      const color = Utils.getDecodedHexForGraphColor(option.color) || {
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length] + (isLine ? '20' : '')
      };

      const data = labels.map(label => getMetricValueAtLabel(dataPoints, label, option.label));

      const sampleYValue = dataPoints
        .flatMap(dp => dp.yValues || [])
        .find(y => y.label === option.label);

      const translatedLabel = getTranslatedLabelWithUnit(
        sampleYValue?.localizedContents,
        i18n.language,
        'label',
        option.label,
        option.unitOfMeasure
      );

      if (isLine) {
        return {
          label: translatedLabel,
          data,
          borderColor: color.borderColor,
          backgroundColor: color.backgroundColor,
          borderWidth: 3,
          fill: config?.fill || false,
          tension: 0.4,
          pointBackgroundColor: 'transparent',
          pointBorderColor: color.borderColor,
          pointBorderWidth: 2,
          pointRadius: 4,
          pointHoverRadius: 6,
          unitOfMeasure: option.unitOfMeasure || kpiUom,
        };
      }

      return {
        label: translatedLabel,
        data,
        backgroundColor: (context) => {
          const { ctx, chartArea } = context.chart;
          if (!chartArea) return color.backgroundColor;

          const gradient = ctx.createLinearGradient(
            chartArea.left, chartArea.bottom,
            chartArea.left, chartArea.top
          );
          gradient.addColorStop(0, color.backgroundColor);
          gradient.addColorStop(1, color.backgroundColor + '90');
          return gradient;
        },
        borderColor: color.borderColor,
        borderWidth: 0,
        borderRadius: 4,
        maxBarThickness: 28,
        stack: (graphData.type || config.type || '')?.includes('STACKED') ? 'stack' : undefined,
        unitOfMeasure: option.unitOfMeasure || kpiUom,
      };
    });

    setChartData({ labels, datasets });
    setChartType(graphData.type || config.type || defaultChartType);
    setYAxisMax(calculateYAxisMax({ labels, datasets }));

    if (!graphData.footers || graphData.footers.length === 0) {
      const summary = calculateSummaryData({ labels, datasets });
      setSummaryData(summary);
    }
  };

  const setupLineChartData = (graphData) => {
    buildBarLineChartData(graphData, 'LINE');
  };

  const setupBarChartData = (graphData) => {
    buildBarLineChartData(graphData, 'BAR');
  };

  const setupPieChartData = (graphData) => {
    const dataPoints = graphData.dataPoints || [];
    const dataPointOptions = graphData.dataPointOptions?.filter(o => !o.position) || [];

    // For pie/doughnut charts, we need to aggregate data across all years
    // Group data by base metric type and aggregate values across years
    const baseMetrics = new Map();
    
    // If no data points, create sample data for testing
    if (dataPoints.length === 0) {      
      const sampleData = {
        labels: ['Energy (GJ)', 'Water (m3)', 'Waste (tons)'],
        datasets: [{
          data: [250, 450, 125],
          backgroundColor: ['#3b82f6', '#10b981', '#f59e0b'],
          borderColor: ['#3b82f6', '#10b981', '#f59e0b'],
          borderWidth: 2,
          unitOfMeasure: 'Mixed',
          yearLabels: ['2023, 2024', '2023, 2024', '2023, 2024']
        }]
      };
      
      setChartData(sampleData);
      setChartType(graphData.type || config.type || 'PIE');
      setYAxisMax(calculateYAxisMax(sampleData));
      
      const summary = calculateSummaryData(sampleData);
      setSummaryData(summary);
      return;
    }
    
    dataPoints.forEach((dp) => {
      if (dp.yValues && dp.yValues.length > 0) {
        dp.yValues.forEach(yValue => {
          
          // Extract base label without year suffix - handle multiple formats
          // Safely extract base label and year using helper function to avoid ReDoS
          const { baseLabel: extractedBaseLabel, year: extractedYear } = extractBaseLabelAndYear(yValue.label);
          let baseLabel, year;
          
          if (extractedBaseLabel !== null && extractedYear !== null) {
            baseLabel = extractedBaseLabel;
            year = extractedYear;
          } else {
            // Handle case where there's no year suffix
            baseLabel = yValue.label.trim();
            year = new Date().getFullYear(); // Default to current year
          }
          
          if (!baseMetrics.has(baseLabel)) {
            // Find the corresponding dataPointOption to get the color and UOM
            const dataPointOption = dataPointOptions.find(option => 
              option.label === baseLabel || yValue.label.includes(option.label)
            );
            
            baseMetrics.set(baseLabel, {
              label: baseLabel,
              totalValue: 0,
              color: dataPointOption?.color || yValue.color,
              order: yValue.order || 0,
              unitOfMeasure: dataPointOption?.unitOfMeasure || yValue.unitOfMeasure,
              localizedContents: yValue.localizedContents,
              years: new Set()
            });
          }
          
          const metric = baseMetrics.get(baseLabel);
          const value = parseFloat(yValue.value) || 0;
          metric.totalValue += value;
          metric.years.add(year);
                  
        });
      }
    });

    // Convert to arrays for chart rendering
    const labels = [];
    const data = [];
    const backgroundColors = [];
    const yearLabels = [];

    // Sort metrics by order and create chart data
    Array.from(baseMetrics.values())
      .sort((a, b) => a.order - b.order)
      .forEach((metric, index) => {
        // Create label with aggregated value and year range
        const yearRange = Array.from(metric.years).sort().join(', ');
        const translatedLabel = getTranslatedLabelWithUnit(
          metric.localizedContents, 
          i18n.language, 
          'label', 
          metric.label, 
          metric.unitOfMeasure
        );
        
        // Add year range to label if multiple years
        const finalLabel = metric.years.size > 1 ? 
          `${translatedLabel} (${yearRange})` : 
          translatedLabel;
        
        labels.push(finalLabel);
        data.push(metric.totalValue);
        
        // Same color resolution as ModernChartCard: Utils first, then default colors array
        const color = Utils.getDecodedHexForGraphColor(metric.color);
        backgroundColors.push(color?.backgroundColor ?? colors[index % colors.length]);
        yearLabels.push(yearRange);
      });

    // Filter out zero values to avoid empty pie slices
    // Be more lenient - only filter if ALL values are zero
    const hasValidData = data.some(value => value > 0);
    
    if (!hasValidData || data.length === 0) {
      // If no data, show a fallback
      labels.length = 0;
      data.length = 0;
      backgroundColors.length = 0;
      yearLabels.length = 0;
      
      labels.push(t("chart.noData.label") || 'No Data');
      data.push(1);
      backgroundColors.push('#e5e7eb');
      yearLabels.push('');
    } else {
      // Filter out only zero values, keep any non-zero values
      const nonZeroIndices = data.map((value, index) => ({ value, index })).filter(item => item.value > 0);
      
      if (nonZeroIndices.length !== data.length) {
        // Filter out zero values
        const filteredLabels = nonZeroIndices.map(item => labels[item.index]);
        const filteredData = nonZeroIndices.map(item => data[item.index]);
        const filteredColors = nonZeroIndices.map(item => backgroundColors[item.index]);
        const filteredYearLabels = nonZeroIndices.map(item => yearLabels[item.index]);
        
        labels.length = 0;
        data.length = 0;
        backgroundColors.length = 0;
        yearLabels.length = 0;
        
        labels.push(...filteredLabels);
        data.push(...filteredData);
        backgroundColors.push(...filteredColors);
        yearLabels.push(...filteredYearLabels);
      }
    }

    const pieXValue = dataPoints[0]
      ? formatFyChartXAxisLabel(dataPoints[0].xValue || '', dataPoints[0].year, isFinancialYear)
      : '';

    setChartData({
      labels,
      xValue: pieXValue,
      datasets: [{
        data: data,
        backgroundColor: backgroundColors,
        borderColor: backgroundColors.map(color => color + '80'),
        borderWidth: 2,
        unitOfMeasure: kpiUom,
        yearLabels: yearLabels // Store year information for tooltips
      }]
    });

    setChartType(graphData.type || config.type || 'PIE');
    setYAxisMax(calculateYAxisMax({ labels, datasets: [{ data: data, backgroundColor: backgroundColors }] }));

    if (!graphData.footers || graphData.footers.length === 0) {
      const summary = calculateSummaryData({ labels, datasets: [{ data: data, backgroundColor: backgroundColors }] });
      setSummaryData(summary);
    }
    
    // Set KPI UOM from the first metric if available
    if (baseMetrics.size > 0) {
      const firstMetric = Array.from(baseMetrics.values())[0];
      setKpiUom(firstMetric.unitOfMeasure || kpiUom);
    }
  };

  const setupKPIChartData = (graphData) => {
    // For KPI, we'll show the sum of all data points as a large number
    const dataPoints = graphData.dataPoints || [];
    const dataPointOptions = graphData.dataPointOptions?.filter(o => !o.position) || [];

    // Extract UOM from dataPointOptions
    const uom = graphData.dataPointOptions?.[0]?.unitOfMeasure || '';
    setKpiUom(uom);

    // Extract color from dataPointOptions
    const color = Utils.getDecodedHexForGraphColor(dataPointOptions[0]?.color);
    setKpiColor(color);

    if (dataPoints.length > 0 && dataPointOptions.length > 0) {
      // Calculate the sum of all data points for the first dataPointOption
      let totalValue = 0;
      let translatedLabel = '';

      // For KPI charts, always use current year data only
      // Since we're already fetching only current year data in fetchChartData,
      // we can simply sum all the data points without year filtering
      dataPoints.forEach((dp) => {
        if (dp.yValues && dp.yValues.length > 0) {
          // Find the matching yValue by checking if the label starts with the option label
          // This handles cases where the yValue label has a year suffix like "(2024)"
          const value = dp.yValues.find(y => y.label.startsWith(dataPointOptions[0].label))?.value ?? 0;
          totalValue += value;
        }
      });

      // Get the label from the first dataPointOption
      translatedLabel = getTranslatedLabelWithUnit(dataPointOptions[0]?.localizedContents, i18n.language, 'label', dataPointOptions[0].label, dataPointOptions[0].unitOfMeasure);

      const chartData = {
        labels: [translatedLabel],
        datasets: [{
          data: [totalValue],
          backgroundColor: colors[0],
          borderColor: colors[0],
          unitOfMeasure: uom,
        }]
      };

      setChartData(chartData);
      setChartType(graphData.type || config.type || 'KPI');
      setYAxisMax(calculateYAxisMax(chartData));

      // Calculate and set summary data only if footer data is not available
      if (!graphData.footers || graphData.footers.length === 0) {
        const summary = calculateSummaryData({ labels: [translatedLabel], datasets: [{ data: [totalValue], backgroundColor: colors[0], borderColor: colors[0] }] });
        setSummaryData(summary);
      }
    } else {
      // Fallback for no data
      const fallbackData = {
        labels: ['No Data'],
        datasets: [{ data: [0], backgroundColor: colors[0], borderColor: colors[0] }]
      };
      setChartData(fallbackData);
      setChartType(graphData.type || config.type || 'KPI');
      setYAxisMax(calculateYAxisMax(fallbackData));

      // Calculate and set summary data only if footer data is not available
      if (!graphData.footers || graphData.footers.length === 0) {
        const summary = calculateSummaryData(fallbackData);
        setSummaryData(summary);
      }
    }
  };

  const setupAreaChartData = (graphData) => {
    let dataPoints = graphData.dataPoints?.sort((a, b) => a.order - b.order) || [];
    const dataPointOptions = graphData.dataPointOptions?.filter(o => !o.position) || [];

    // Limit data points for better performance in modal
    if (dataPoints.length > 24) {
      dataPoints = dataPoints.slice(0, 24);
    }

    const labels = [];
    const datasets = [];

    // For MONTHLY in modal: show all months in filter range; else only months with data
    const uniqueLabels = new Set();
    if (isMonthyInterval(graphData) && filters?.periodFrom && filters?.periodTo) {
      labels.push(...getAllMonthLabelsInRange(filters.periodFrom, filters.periodTo));
    } else {
      dataPoints?.forEach(dp => {
        if (isMonthyInterval(graphData)) {
          dp.yValues?.forEach(yValue => {
            const match = yValue.label.match(/\((\d{4})\)$/);
            if (match && yValue.value > 0) {
              const year = parseInt(match[1]);
              const date = new Date(`${dp.xValue} 1, ${year}`);
              if (!isNaN(date.getTime())) {
                const monthName = new Intl.DateTimeFormat('en', { month: 'short' }).format(date);
                uniqueLabels.add(`${year} ${monthName}`);
              }
            }
          });
        } else {
          labels.push(
            isFyYearlyXAxisValue(dp.xValue)
              ? formatFyChartXAxisLabel(dp.xValue, dp.year, isFinancialYear)
              : dp.xValue
          );
        }
      });
      if (isMonthyInterval(graphData)) {
        const sortedLabels = Array.from(uniqueLabels).sort((a, b) => {
          const parsedA = parseYearAndMonth(a);
          const parsedB = parseYearAndMonth(b);
          if (parsedA && parsedB) {
            if (parsedA.year !== parsedB.year) return parsedA.year - parsedB.year;
            const monthA = new Date(`${parsedA.month} 1, ${parsedA.year}`).getMonth();
            const monthB = new Date(`${parsedB.month} 1, ${parsedB.year}`).getMonth();
            return monthA - monthB;
          }
          return a.localeCompare(b);
        });
        labels.push(...sortedLabels);
      }
    }

    // Group data by base metric type (with year included) - similar to bar chart logic
    const baseMetrics = new Map();
    
    dataPoints.forEach((dp, dpIndex) => {
      dp.yValues?.forEach(yValue => {
        // Safely extract base label and year using helper function to avoid ReDoS
        const { baseLabel: extractedBaseLabel, year: extractedYear } = extractBaseLabelAndYear(yValue.label);
        if (extractedBaseLabel !== null && extractedYear !== null) {
          const baseLabel = extractedBaseLabel;
          const year = extractedYear;
          const fullLabel = `${baseLabel} (${year})`;
          
          if (!baseMetrics.has(fullLabel)) {
            // Find the corresponding dataPointOption to get the color
            const dataPointOption = dataPointOptions.find(option => 
              option.label === baseLabel || yValue.label.includes(option.label)
            );
            
            baseMetrics.set(fullLabel, {
              label: fullLabel,
              data: new Array(labels.length).fill(0),
              color: dataPointOption?.color || yValue.color,
              order: yValue.order,
              year: year,
              localizedContents: yValue.localizedContents,
              unitOfMeasure: dataPointOption?.unitOfMeasure
            });
          }
          
          const metric = baseMetrics.get(fullLabel);
          // Find the correct label index for this year-month combination
          const monthName = new Intl.DateTimeFormat('en', { month: 'short' }).format(new Date(`${dp.xValue} 1, ${year}`));
          const labelKey = `${year} ${monthName}`;
          const labelIndex = labels.indexOf(labelKey);
          
          if (labelIndex !== -1) {
            let value = yValue.value;

            // Apply filtering for current year data
            if (isMonthyInterval(graphData) && year === new Date().getFullYear()) {
              const monthIndex = new Date(`${dp.xValue} 1, ${year}`).getMonth();
              if (monthIndex > new Date().getMonth()) {
                value = 0; // Hide future months in current year
              }
            }

            metric.data[labelIndex] = value;
          }
        }
      });
    });

    // Create datasets for each base metric
    Array.from(baseMetrics.values())
      .sort((a, b) => {
        // Sort by year first, then by order
        if (a.year !== b.year) {
          return a.year - b.year;
        }
        return a.order - b.order;
      })
      .forEach((metric, index) => {
        // Same color resolution as ModernChartCard: Utils first, then default colors array
        const color = Utils.getDecodedHexForGraphColor(metric.color) || {
          borderColor: colors[index % colors.length],
          backgroundColor: colors[index % colors.length] + '20'
        };

        // Create translated label for the dataset
        const translatedLabel = getTranslatedLabelWithUnit(
          metric.localizedContents, 
          i18n.language, 
          'label', 
          metric.label, 
          metric.unitOfMeasure
        );

        datasets.push({
          label: translatedLabel,
          data: metric.data,
          borderColor: color.borderColor,
          backgroundColor: (context) => {
            const { ctx, chartArea } = context.chart;
            if (!chartArea) return null;
            const gradient = ctx.createLinearGradient(
              chartArea.left, chartArea.top,
              chartArea.right, chartArea.bottom
            );
            gradient.addColorStop(0, color.borderColor);
            gradient.addColorStop(1, color.backgroundColor);
            return gradient;
          },
          borderWidth: 2,
          fill: true,
          tension: 0.4,
          pointBackgroundColor: 'transparent',
          pointBorderColor: 'transparent',
          pointRadius: 0,
          pointHoverRadius: 6,
          pointHoverBackgroundColor: color.borderColor,
          pointHoverBorderColor: '#ffffff',
          pointHoverBorderWidth: 2,
          unitOfMeasure: metric.unitOfMeasure || kpiUom,
        });
      });

    setChartData({ labels, datasets });
    setChartType(graphData.type || 'AREA');
    setYAxisMax(calculateYAxisMax({ labels, datasets }));

    if (!graphData.footers || graphData.footers.length === 0) {
      const summary = calculateSummaryData({ labels, datasets });
      setSummaryData(summary);
    }
  };

  const calculateYAxisMax = (data) => {
    if (!data || !data.datasets) return null;

    let maxValue = 0;

    // Match ModernChartCard: stacked bars must use per-column totals, not the tallest segment
    const isStacked = data.datasets.some(
      (dataset) => dataset.stack !== undefined && dataset.stack !== null
    );

    if (isStacked) {
      const numDataPoints = data.datasets[0]?.data?.length || 0;

      for (let i = 0; i < numDataPoints; i++) {
        const segmentValues = data.datasets.map((dataset) => dataset.data?.[i]);
        const stackTotal = sumStackedValues(segmentValues);
        maxValue = Math.max(maxValue, stackTotal);
      }
    } else {
      data.datasets.forEach((dataset) => {
        if (dataset.data && Array.isArray(dataset.data)) {
          const validValues = dataset.data.filter(
            (val) => val !== null && val !== undefined && !isNaN(val)
          );
          if (validValues.length > 0) {
            maxValue = Math.max(maxValue, Math.max(...validValues));
          }
        }
      });
    }

    if (maxValue > 0) {
      return roundAxisMax(maxValue * 1.15);
    }

    return null;
  };

  const generateSampleData = () => {
    const labels = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    const datasets = [{
      label: 'Sample Data',
      data: [65, 59, 80, 81, 56, 55],
      backgroundColor: colors[0],
      borderColor: colors[0],
      borderWidth: 1,
    }];

    const chartData = { labels, datasets };
    setChartData(chartData);

    const summary = calculateSummaryData(chartData);
    setSummaryData(summary);

    setError(null);
  };

  const getLocalizedLabel = (localizedContents, languageCode, fieldName) => {
    if (!Array.isArray(localizedContents)) return null;
    const localized = localizedContents.find(
      (content) => content.languageCode === languageCode && content.fieldName === fieldName
    );
    return localized ? localized.translatedValue : null;
  };

  // Helper function to append unit to translated label
  const getTranslatedLabelWithUnit = (localizedContents, languageCode, fieldName, fallbackLabel, unit) => {
    const translatedLabel = getLocalizedLabel(localizedContents, languageCode, fieldName) ?? fallbackLabel;
    return unit ? `${translatedLabel} (${unit})` : translatedLabel;
  };

  // Helper function to extract base label from year-suffixed labels
  const extractBaseLabel = (label) => {
    if (!label) return '';
    // Remove year suffix like " (2024)" or " (2025)" - can be anywhere in the label
    // Also remove unit suffix like " (GJ)" or " (tons)" that might come after the year
    let baseLabel = label.trim();
    
    // Safely remove suffixes using string methods to avoid ReDoS vulnerabilities
    // Process from the end to handle nested or multiple suffixes
    let changed = true;
    while (changed && baseLabel.endsWith(')')) {
      changed = false;
      const lastOpenParen = baseLabel.lastIndexOf('(');
      if (lastOpenParen !== -1 && lastOpenParen < baseLabel.length - 1) {
        const content = baseLabel.slice(lastOpenParen + 1, -1);
        // Limit content length to prevent ReDoS (max 50 chars for safety)
        // Remove suffix if it's a year (exactly 4 digits) or unit (any other content)
        if (content.length > 0 && content.length < 50) {
          // Use anchored regex to prevent backtracking: ^\d{4}$ is safe (no backtracking possible)
          // Remove the suffix (either year or unit)
          baseLabel = baseLabel.slice(0, lastOpenParen).trim();
          changed = true;
        }
      }
    }
    
    return baseLabel;
  };

  // Helper function to extract year from label
  const extractYear = (label) => {
    if (!label) return '';
    const match = label.match(/\((\d{4})\)$/);
    return match ? match[1] : '';
  };

  // Helper function to safely extract base label and year from pattern "label (YYYY)"
  // Uses string methods to avoid ReDoS vulnerabilities from regex backtracking
  const extractBaseLabelAndYear = (label) => {
    if (!label) return { baseLabel: null, year: null };
    
    // Check if label ends with closing parenthesis
    if (!label.endsWith(')')) {
      return { baseLabel: null, year: null };
    }
    
    // Find the last opening parenthesis
    const lastOpenParen = label.lastIndexOf('(');
    if (lastOpenParen === -1 || lastOpenParen >= label.length - 1) {
      return { baseLabel: null, year: null };
    }
    
    // Extract content between parentheses
    const content = label.slice(lastOpenParen + 1, -1);
    
    // Check if content is exactly 4 digits (year) - limit length to prevent ReDoS
    if (content.length === 4 && /^\d{4}$/.test(content)) {
      const year = parseInt(content, 10);
      const baseLabel = label.slice(0, lastOpenParen).trim();
      return { baseLabel, year };
    }
    
    return { baseLabel: null, year: null };
  };

  // Helper function to safely parse year and month from labels like "2024 Jan"
  // Uses string methods to avoid ReDoS vulnerabilities from regex backtracking
  const parseYearAndMonth = (label) => {
    if (!label || typeof label !== 'string') return null;
    
    // Find first whitespace character
    const firstSpaceIndex = label.indexOf(' ');
    if (firstSpaceIndex === -1 || firstSpaceIndex < 4) return null;
    
    // Extract year (first 4 characters should be digits)
    const yearStr = label.slice(0, 4);
    if (!/^\d{4}$/.test(yearStr)) return null;
    
    // Extract month (everything after the first space, limit length to prevent ReDoS)
    const monthStr = label.slice(firstSpaceIndex + 1).trim();
    if (monthStr.length === 0 || monthStr.length > 50) return null; // Limit length
    
    return {
      year: parseInt(yearStr, 10),
      month: monthStr
    };
  };

  // Generate all month labels between periodFrom and periodTo (e.g. "April 2025")
  const getAllMonthLabelsInRange = (periodFrom, periodTo) => {
    if (!periodFrom || !periodTo) return [];
    const from = moment(periodFrom).startOf('month');
    const to = moment(periodTo).startOf('month');
    if (from.isAfter(to)) return [];
    const labels = [];
    const current = from.clone();
    while (current.isSameOrBefore(to)) {
      labels.push(`${current.format('MMMM')} ${current.format('YYYY')}`);
      current.add(1, 'month');
    }
    return labels;
  };

  // Helper function to create deduplicated legend labels
  const createDeduplicatedLegendLabels = (datasets) => {
    const baseLabels = new Map();
    
    datasets.forEach((dataset, index) => {
      // Extract base label without year suffix and unit
      const baseLabel = extractBaseLabel(dataset.label);
      
      if (!baseLabels.has(baseLabel)) {
        baseLabels.set(baseLabel, {
          label: baseLabel,
          color: dataset.borderColor || dataset.backgroundColor,
          datasets: []
        });
      }
      baseLabels.get(baseLabel).datasets.push(index);
    });
    
    return Array.from(baseLabels.values());
  };

  const isMonthyInterval = (g) => {
    return g?.timeInterval === 'MONTHLY' || 
           g?.properties?.options?.timeInterval === 'MONTHLY' ||
           g?.options?.timeInterval === 'MONTHLY';
  };

  // Function to toggle between MONTHLY and YEARLY intervals
  const handleToggleInterval = () => {
    const newInterval = activeInterval === 'YEARLY' ? 'MONTHLY' : 'YEARLY';
    setActiveInterval(newInterval);
  };

  const calculateSummaryData = (data) => {
    if (!data || !data.datasets || data.datasets.length === 0) {
      return {
        total: 0,
        percentageChange: 0,
        maxMonth: '',
        minMonth: '',
        unit: kpiUom || 'tons',
        comparisonStyle: 'HIGHER THE BETTER',
        hasIncreased: false
      };
    }

    let total = 0;
    const allData = [];

    data.datasets.forEach(dataset => {
      if (dataset.data && Array.isArray(dataset.data)) {
        dataset.data.forEach((value, index) => {
          if (value !== null && value !== undefined && !isNaN(value)) {
            total += value;
            allData.push({ value, month: data.labels[index] });
          }
        });
      }
    });

    let maxMonth = '';
    let minMonth = '';
    let maxValue = -Infinity;
    let minValue = Infinity;

    allData.forEach(item => {
      if (item.value > maxValue) {
        maxValue = item.value;
        maxMonth = item.month;
      }
      if (item.value < minValue) {
        minValue = item.value;
        minMonth = item.month;
      }
    });

    const percentageChange = allData.length > 1 ?
      ((maxValue - minValue) / minValue * 100).toFixed(1) : 0;

    const comparisonStyle = config?.comparisonStyle || 'HIGHER THE BETTER';
    const hasIncreased = parseFloat(percentageChange) > 0;

    return {
      total: Math.round(total),
      percentageChange: parseFloat(percentageChange),
      maxMonth,
      minMonth,
      unit: kpiUom || 'tons',
      comparisonStyle,
      hasIncreased
    };
  };

  const processFooterData = (footers) => {
    if (!footers || footers.length === 0) return;

    const totalFooter = footers.find(f => f.position === 'BOTTOM LEFT');
    const comparisonFooter = footers.find(f => f.position === 'BOTTOM RIGHT');

    if (totalFooter) {
      const summaryData = {
        total: totalFooter.value || 0,
        unit: totalFooter.uom || kpiUom || 'tons',
        maxMonth: '',
        minMonth: '',
        percentageChange: 0,
        comparisonStyle: 'HIGHER THE BETTER',
        hasIncreased: false
      };

      if (comparisonFooter && comparisonFooter.comparison) {
        const currentValue = comparisonFooter.currentValue || 0;
        const comparedValue = comparisonFooter.comparedValue || 0;
        const hasIncreased = currentValue > comparedValue;

        const percentageChange = comparisonFooter.value || 0;

        summaryData.percentageChange = parseFloat(percentageChange);
        summaryData.hasIncreased = hasIncreased;
        summaryData.comparisonStyle = comparisonFooter.comparisonStyle || 'HIGHER THE BETTER';
      }

      setSummaryData(summaryData);
    }
  };

  const getChartOptions = () => {
    const baseOptions = {
      responsive: true,
      maintainAspectRatio: false,
      animation: {
        duration: 300 // Reduce animation duration for better performance
      },
      layout: {
        padding: {
          top: 20,
          right: 20,
          bottom: 20,
          left: 20
        }
      },
      plugins: {
        legend: {
          display: showLegend,
          position: 'bottom',
          labels: {
            usePointStyle: true,
            pointStyle: 'rect',
            padding: 20,
            font: {
              family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              size: 14, // Increased font size for better readability
            },
            color: '#6b7280',
            // Custom legend label formatter to deduplicate similar labels
            generateLabels: (chart) => {
              const datasets = chart.data.datasets;
              if (!datasets || datasets.length === 0) {
                return [];
              }

              // For PIE and DOUGHNUT: one dataset, segment labels in chart.data.labels — show one legend item per slice
              if (chartType === 'PIE' || chartType === 'DOUGHNUT') {
                const labels = chart.data.labels || [];
                const ds = datasets[0];
                if (!ds) return [];
                const colors = ds.backgroundColor;
                const colorArr = Array.isArray(colors) ? colors : [colors];
                const meta = chart.getDatasetMeta(0);
                return labels.map((label, i) => {
                  const hidden = meta?.data?.[i]?.hidden === true;
                  return {
                    text: label,
                    fillStyle: colorArr[i] || colorArr[0] || '#6b7280',
                    strokeStyle: colorArr[i] || colorArr[0] || '#6b7280',
                    lineWidth: 0,
                    hidden,
                    index: i,
                    datasetIndex: 0
                  };
                });
              }

              // Bar/Line: multiple datasets with deduplicated legend
              const validDatasets = datasets.filter(dataset =>
                dataset.label &&
                dataset.label.trim() !== '' &&
                extractBaseLabel(dataset.label).trim() !== ''
              );

              if (validDatasets.length === 0) {
                return [];
              }

              const deduplicatedLabels = createDeduplicatedLegendLabels(validDatasets);

              return deduplicatedLabels.map((item, index) => {
                const firstDatasetIndex = item.datasets[0];
                const isHidden = chart.data.datasets[firstDatasetIndex]?.hidden === true;
                return {
                  text: item.label,
                  fillStyle: item.color,
                  strokeStyle: item.color,
                  lineWidth: 0,
                  hidden: isHidden,
                  index: index,
                  datasetIndex: firstDatasetIndex,
                  relatedDatasetIndices: item.datasets
                };
              });
            }
          },
          // Custom legend click handler: toggle segment (PIE/DOUGHNUT) or datasets (Bar/Line)
          onClick: (event, legendItem, legend) => {
            const chart = legend.chart;
            if (chartType === 'PIE' || chartType === 'DOUGHNUT') {
              const meta = chart.getDatasetMeta(0);
              if (meta?.data?.[legendItem.index] !== undefined) {
                meta.data[legendItem.index].hidden = !meta.data[legendItem.index].hidden;
                chart.update();
              }
              return;
            }
            const relatedIndices = legendItem.relatedDatasetIndices || [legendItem.datasetIndex];
            relatedIndices.forEach(datasetIndex => {
              const dataset = chart.data.datasets[datasetIndex];
              if (dataset) {
                dataset.hidden = !dataset.hidden;
              }
            });
            chart.update();
          }
        },
        datalabels: {
          display: showLabels,
          color: '#374151',
          font: {
            family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            size: 12, // Increased font size
            weight: '600',
          },
          anchor: 'end',
          align: 'top',
          offset: 4,
          formatter: (value) => {
            if (value === 0) return '';
            return formatNumberWithCommas(value);
          },
        },
        tooltip: {
          enabled: showTooltip,
          backgroundColor: 'rgba(255, 255, 255, 0.95)',
          titleColor: '#1a202c',
          bodyColor: '#374151',
          borderColor: 'rgba(255, 255, 255, 0.3)',
          borderWidth: 1,
          cornerRadius: 12,
          displayColors: true,
          titleFont: {
            family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            size: 16, // Increased font size
            weight: '600',
          },
          bodyFont: {
            family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            size: 14, // Increased font size
          },
          footerFont: {
            family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
            size: 14,
            weight: 'bold',
          },
          footerColor: '#1a202c',
          padding: 16, // Increased padding
          boxPadding: 8,
          // Performance optimization: reduce tooltip complexity
          mode: 'index',
          intersect: false,
          // Custom tooltip formatter to simplify labels
          callbacks: {
            title: (context) => {
              // Show the time period (e.g., "2024 Jan")
              return context[0]?.label || '';
            },
            label: function(context) {
              let label = context.dataset.label || '';
              if (label) {
                label += ': ';
              }
              
              // Handle different chart types for tooltip values
              let value;
              if (chartType === 'PIE' || chartType === 'DOUGHNUT') {
                value = context.parsed;
                const data = context.chart?.data?.datasets?.[0]?.data || [];
                const total = data.reduce((sum, v) => sum + (Number(v) || 0), 0);
                if (value !== null && value !== undefined && !isNaN(value) && total > 0) {
                  const pct = (value / total) * 100;
                  label += `${formatNumberWithCommas(value)} (${pct % 1 === 0 ? pct : pct.toFixed(1)}%)`;
                } else {
                  label += formatNumberWithCommas(value ?? 0);
                }
                return label;
              }
              if (chartType === 'HORIZONTAL BAR' || chartType === 'HORIZONTAL STACKED BAR') {
                value = context.parsed.x;
              } else {
                value = context.parsed.y;
              }
              if (value !== null && value !== undefined && !isNaN(value)) {
                if (chartType !== 'KPI') {
                  label += formatNumberWithCommas(value);
                } else {
                  label += value.toLocaleString();
                }
              } else {
                label += '0';
              }
              return label;
            },
            footer: function(tooltipItems) {
              // Show total for stacked bar charts (same as normal view)
              if (chartType === 'STACKED BAR' || chartType === 'HORIZONTAL STACKED BAR') {
                if (!tooltipItems || tooltipItems.length === 0) return '';
                const dataIndex = tooltipItems[0].dataIndex;
                const datasets = tooltipItems[0].chart.data.datasets;
                const segmentValues = datasets.map((dataset) => dataset.data[dataIndex]);
                const total = sumStackedValues(segmentValues);
                if (total > 0) {
                  const formattedTotal = chartType !== 'KPI'
                    ? formatNumberWithCommas(total)
                    : total.toLocaleString();
                  return `Total: ${formattedTotal}`;
                }
              }
              return '';
            }
          }
        }
      },
      elements: {
        point: {
          hoverRadius: 8, // Increased hover radius
          hoverBorderWidth: 3,
        }
      }
    };

    if (chartType === 'HORIZONTAL BAR' || chartType === 'HORIZONTAL STACKED BAR') {
      baseOptions.indexAxis = 'y';

      baseOptions.plugins.datalabels = {
        ...baseOptions.plugins.datalabels,
        anchor: 'center',
        align: 'center',
        color: '#374151',
        font: {
          family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          size: 12,
          weight: '600',
        },
        formatter: (value) => {
          if (value === 0) return '';
          return formatNumberWithCommas(value);
        },
      };

      // Let Chart.js use category labels from chart.data.labels on the y-axis (same as ModernChartCard)
      return baseOptions;
    }

    // Hide scales for PIE and DOUGHNUT charts; show percentage on slices
    if (chartType === 'PIE' || chartType === 'DOUGHNUT') {
      baseOptions.scales = {
        x: {
          display: false
        },
        y: {
          display: false
        }
      };
      baseOptions.plugins.datalabels = {
        ...baseOptions.plugins.datalabels,
        display: true,
        anchor: 'center',
        align: 'center',
        color: '#374151',
        font: {
          family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          size: 12,
          weight: '600',
        },
        formatter: (value, context) => {
          if (value === 0) return '';
          const dataset = context.chart?.data?.datasets?.[0];
          const data = dataset?.data || [];
          const total = data.reduce((sum, v) => sum + (Number(v) || 0), 0);
          if (total === 0) return '';
          const pct = ((value / total) * 100);
          return pct % 1 === 0 ? `${pct}%` : `${pct.toFixed(1)}%`;
        },
      };
    } else if (chartType === 'STACKED BAR') {
      // Regular stacked bar (vertical) — respect showLabels toggle (same as ModernChartCard)
      baseOptions.plugins.datalabels = {
        ...baseOptions.plugins.datalabels,
        display: showLabels,
        // Keep all segment values centered inside segments (original behavior)
        anchor: 'center',
        align: 'center',
        offset: 0,
        clamp: true,
        color: '#374151',
        font: {
          family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          size: 12,
          weight: '600',
        },
        formatter: (value, context) => {
          // Always show individual segment values inside ALL segments (including last one)
          if (value === 0) return '';
          
          // Show individual values for all segments (preserve existing behavior)
          if (chartType !== 'KPI') {
            return formatNumberWithCommas(value);
          }
          return value.toLocaleString();
        },
      };
      
      baseOptions.scales = {
        x: {
          display: true,
          grid: {
            display: false, // Hide grid lines
            color: 'rgba(229, 231, 235, 0.5)',
            drawBorder: true, // Show X axis border
            borderColor: '#e5e7eb',
            borderWidth: 1,
          },
          ticks: {
            color: '#6b7280',
            font: {
              family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              size: 14, // Increased font size
            },
          },
          stacked: true
        },
        y: {
          display: true,
          grid: {
            display: false, // Hide grid lines
            color: 'rgba(229, 231, 235, 0.5)',
            drawBorder: true, // Show Y axis border
            borderColor: '#e5e7eb',
            borderWidth: 1,
          },
          ticks: {
            color: '#6b7280',
            font: {
              family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              size: 14, // Increased font size
            },
            // callback: function (value, index, values) {
            //   if (value >= 1000000000) {
            //     return (value / 1000000000).toFixed(1) + 'B';
            //   } else if (value >= 1000000) {
            //     return (value / 1000000).toFixed(1) + 'M';
            //   } else if (value >= 1000) {
            //     return (value / 1000).toFixed(1) + 'K';
            //   }
            //   return value.toLocaleString();
            // }
            callback: function (value, index, values) {
              return Utils.formatYAxisValue(value);
            }
          },
          beginAtZero: true,
          afterFit: function (scale) {
            scale.paddingTop = 30; // Increased padding
          },
          max: yAxisMax
        }
      };
    } else if (chartType !== 'HORIZONTAL BAR' && chartType !== 'HORIZONTAL STACKED BAR') {
      baseOptions.scales = {
        x: {
          display: true,
          grid: {
            display: false, // Hide grid lines
            color: 'rgba(229, 231, 235, 0.5)',
            drawBorder: true, // Show X axis border
            borderColor: '#e5e7eb',
            borderWidth: 1,
          },
          ticks: {
            color: '#6b7280',
            font: {
              family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              size: 14, // Increased font size
            },
          }
        },
        y: {
          display: true,
          grid: {
            display: false, // Hide grid lines
            color: 'rgba(229, 231, 235, 0.5)',
            drawBorder: true, // Show Y axis border
            borderColor: '#e5e7eb',
            borderWidth: 1,
          },
          ticks: {
            color: '#6b7280',
            font: {
              family: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
              size: 12,
            },
            callback: function (value, index, values) {
              return Utils.formatYAxisValue(value);
            }
          },
          beginAtZero: true,
          afterFit: function (scale) {
            scale.paddingTop = 20;
          },
          max: yAxisMax
        }
      };
    }

    return baseOptions;
  };

  // Use useMemo to regenerate chart options when chartData changes
  const chartOptions = React.useMemo(() => getChartOptions(), [chartData, chartType, showGrid, showLegend, showTooltip, yAxisMax, showLabels, year]);

  // Create custom plugin for stacked bar total on top
  // Include chartData in dependencies to ensure plugin updates when data changes
  // Stacked bar total on top — only when showLabels is enabled (same as ModernChartCard)
  const stackedBarTotalPlugin = React.useMemo(() => {
    const currentChartType = chartType || config?.type || type;
    const normalizedType = (currentChartType || '').toUpperCase();
    if (normalizedType !== 'STACKED BAR' || !showLabels) return null;
    
    const dataLabelColor = '#374151';
    
    return {
      id: 'stackedBarTotal',
      afterDraw: (chart) => {
        // Only process stacked bar charts
        if (!chart || !chart.data || !chart.data.datasets) return;
        
        const ctx = chart.ctx;
        const datasets = chart.data.datasets;
        if (!datasets || datasets.length === 0) return;
        
        const lastDatasetIndex = datasets.length - 1;
        const lastDataset = datasets[lastDatasetIndex];
        if (!lastDataset || !lastDataset.data) return;
        
        // Get the meta for the last dataset
        const meta = chart.getDatasetMeta(lastDatasetIndex);
        if (!meta || !meta.data || meta.data.length === 0) return;
        
        // Check if labels exist
        const labels = chart.data.labels || [];
        if (labels.length === 0) return;
        
        labels.forEach((label, dataIndex) => {
          const segmentValues = datasets.map((dataset) => dataset.data[dataIndex]);
          const total = sumStackedValues(segmentValues);
          
          if (total > 0 && dataIndex < meta.data.length) {
            // Get the bar element for the last dataset at this data index
            const bar = meta.data[dataIndex];
            if (!bar || typeof bar.x === 'undefined' || typeof bar.y === 'undefined') return;
            
            // For vertical stacked bars:
            // bar.y is the y-coordinate of the top of the last segment (which is the top of the entire stack)
            // bar.x is the x-coordinate (center of the bar)
            const topY = bar.y; // Top of the entire stacked bar
            const x = bar.x; // Center of the bar
            
            // Format the total value
            const normalizedType = (chartType || '').toUpperCase();
            const formattedTotal = normalizedType !== 'KPI' 
              ? formatNumberWithCommas(total)
              : total.toLocaleString();
            
            // Draw the total label on top
            ctx.save();
            ctx.font = `600 14px Inter, -apple-system, BlinkMacSystemFont, sans-serif`;
            ctx.fillStyle = dataLabelColor;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'bottom';
            
            // Draw text above the bar (topY is the top, so subtract offset to position above)
            ctx.fillText(formattedTotal, x, topY - 10);
            ctx.restore();
          }
        });
      }
    };
  }, [chartType, config?.type, type, formatNumberWithCommas, chartData, showLabels]);

  const renderChart = () => {
    if (isLoading) {
      return (
        <div className="maximized-chart-loading">
          <FiRefreshCw className="loading-spinner spinning" />
          <p>{t("loadingChart.label")}</p>
        </div>
      );
    }

    if (error) {
      return (
        <div className="maximized-chart-error">
          <p>{error}</p>
          <button onClick={fetchChartData} className="retry-button">
            {t("retry.label")}
          </button>
        </div>
      );
    }

    if (!chartData) {
      generateSampleData();
      return (
        <div className="maximized-chart-loading">
          <FiRefreshCw className="loading-spinner spinning" />
          <p>{t("loadingChart.label")}</p>
        </div>
      );
    }

    const currentChartType = chartType || config?.type || 'BAR';
    // Normalize chart type for case-insensitive comparison
    const normalizedChartType = currentChartType?.toUpperCase();

    switch (normalizedChartType) {
      case 'LINE':
        return <Line ref={chartRef} data={chartData} options={chartOptions} plugins={[ChartDataLabels]} height={height} />;
      case 'AREA':
        return <Line ref={chartRef} data={chartData} options={chartOptions} plugins={[ChartDataLabels]} height={height} />;
      case 'BAR':
      case 'HORIZONTAL BAR':
      case 'HORIZONTAL STACKED BAR':
        return <Bar ref={chartRef} data={chartData} options={chartOptions} plugins={[ChartDataLabels]} height={height} />;
      case 'STACKED BAR':
        // For stacked bars, always include custom plugin for total on top in maximized view
        // Always create a new plugins array to ensure Chart.js re-registers plugins on update
        const plugins = [ChartDataLabels];
        if (stackedBarTotalPlugin) {
          plugins.push(stackedBarTotalPlugin);
        }
        // Add key to force re-render when data changes
        return <Bar ref={chartRef} data={chartData} options={chartOptions} plugins={plugins} height={height} key={`stacked-bar-${showLabels}-${chartData?.labels?.length || 0}`} />;
      case 'PIE':
      case 'DOUGHNUT':
        return <Doughnut ref={chartRef} data={chartData} options={chartOptions} plugins={[ChartDataLabels]} height={height} />;
      case 'KPI':
        return (
          <div className="maximized-kpi-card" style={{ textAlign: 'center', background: kpiColor?.backgroundColor || '' }}>
            <div className="maximized-kpi-card-content">
              <div className="maximized-kpi-value-row" style={{ justifyContent: 'center' }}>
                <span className="maximized-kpi-value">{chartData.datasets[0]?.data[0]?.toLocaleString() || '0'}</span>
                <span className="maximized-kpi-unit">{kpiUom}</span>
              </div>
              <div className="maximized-kpi-label" style={{ textAlign: 'center' }}>{chartData.labels[0] || t("kpi.noData.label")}</div>
            </div>
          </div>
        );
      default:        
        return <Bar ref={chartRef} data={chartData} options={chartOptions} plugins={[ChartDataLabels]} height={height} />;
    }
  };  

  const roundAxisMax = (paddedMax) => {
    if (paddedMax >= 1000000000) return Math.ceil(paddedMax / 5000) * 5000;
    if (paddedMax >= 1000000) return Math.ceil(paddedMax / 1000) * 1000;
    if (paddedMax >= 1000) return Math.ceil(paddedMax / 100) * 100;
    if (paddedMax >= 1) return Math.ceil(paddedMax);
    if (paddedMax >= 0.1) return Math.ceil(paddedMax * 10) / 10;
    if (paddedMax >= 0.01) return Math.ceil(paddedMax * 100) / 100;
    if (paddedMax >= 0.001) return Math.ceil(paddedMax * 1000) / 1000;
    return Math.ceil(paddedMax * 10000) / 10000;
  };

  return (
    <div className="maximized-chart-card" data-testid="maximized-chart-card">
      <div className="maximized-chart-canvas-container">
        {renderChart()}
      </div>
      {(chartType === 'PIE' || chartType === 'DOUGHNUT') && chartData?.xValue && (
        <div className="text-center" data-testid="chart-card-footer">
          {chartData.xValue}
        </div>
      )}
    </div>
  );
});

// PropTypes for type checking
MaximizedChartCard.propTypes = {
  config: PropTypes.object,
  year: PropTypes.number,
  title: PropTypes.string,
  type: PropTypes.string,
  height: PropTypes.number,
  showLegend: PropTypes.bool,
  showGrid: PropTypes.bool,
  showTooltip: PropTypes.bool,
  colors: PropTypes.array,
  filters: PropTypes.object,
  onDataUpdate: PropTypes.func,
  standardId: PropTypes.number,
  showLabels: PropTypes.bool,
  onShowLabelsChange: PropTypes.func
};

export default MaximizedChartCard; 